import React, { useEffect } from "react";
import { useState } from "react";
import { useGridApiRef, GridCellParams, GridRenderCellParams, GridColumnHeaderParams } from "@mui/x-data-grid";
import { DataGridPro, GridApiPro, GridSlotsComponentsProps } from "@mui/x-data-grid-pro"; // TOOD: get license key, trial for now
import { GridColDef } from "@mui/x-data-grid-pro";
import Box from "@mui/material/Box";
import { LoadingButton } from "@mui/lab";
import Paper from "@mui/material/Paper";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { useTheme } from "@mui/material/styles";

import StockPlan from "./../../StockPlanTypes/StockPlan";
import { Skeleton } from "@mui/material";

import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Tooltip from "@mui/material/Tooltip";

import AsOf from "../UtilityComponents/PaperUtilityComponents/AsOf";
import IconButton from "@mui/material/IconButton";
import EditIcon from "@mui/icons-material/Edit";

import { FormGroup } from "@mui/material";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";

import AddIcon from "@mui/icons-material/Add";

import dayjs, { Dayjs } from "dayjs";
import { LinearProgress, Typography } from "@mui/material";
import LearnTextEmbed from "../LearnPanelComponents/LearnCopy/LearnTextEmbed";
import NoWrapTooltip from "../UtilityComponents/TooltipUtilityComponents/NoWrapTooltip";
import ESPP from "../../ESPPTypes/ESPP";
import { deepmerge } from "@mui/utils";

const firstAndLastColumnsListWidth = 180;
const rowNameWidth = 200;
const buttonWidth = 40;

var advancedFormat = require("dayjs/plugin/advancedFormat");
dayjs.extend(advancedFormat);
var quarterOfYear = require("dayjs/plugin/quarterOfYear");
dayjs.extend(quarterOfYear);

const paperPadding = 2;
const bigFontSize = 16;
const mediumFontSize = 14;
const smallFontSize = 12;
const enterToolTipDelay = 400;

const allGrantsString = "All Grants";

function formatPrice(price: number) {
  if (price === null || price === undefined) {
    return "";
  }
  const localizedPrice = Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(price);
  return localizedPrice;
}

function formatDollars(dollars: number) {
  if (dollars === null || dollars === undefined) {
    return "";
  }
  const localizedDollars = Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(dollars);
  return localizedDollars;
}

function formatPercent(percent: number) {
  const formattedPercentString = `${(percent * 100).toFixed(1)}%`;
  return formattedPercentString;
}

function formatDate(date: Dayjs) {
  return date.format("MMMM DD, YYYY");
}

type dataType = {
  [key: string]: {
    dataValue: string | number | boolean | Dayjs;
    formattedValue: string;
  };
};

function createDataFromESPP(
  espp: ESPP,
  tableHeaders: { [key: string]: keyof ESPP },
  tableHeaderFormatters: { [key: string]: (value: any) => string }
) {
  const data: dataType = {};
  for (const [key, value] of Object.entries(tableHeaders)) {
    if (Array.isArray(espp[value])) {
      continue;
    }
    let formattedValue = tableHeaderFormatters[key](espp[value]);

    data[key] = {
      dataValue: espp[value] as string | number | boolean | Dayjs,
      formattedValue: formattedValue,
    };
  }
  data["percentVested"] = { dataValue: espp.percent_vested, formattedValue: formatPercent(espp.percent_vested) };

  return { name: espp.name, data: data };
}

type ESPPTableProps = {
  esppPlans: ESPP[];
  onStockPlanEdit: (name: string) => void;
  startAdd: () => void;
  referenceDateString: string;
  loadingAddStockPlan: boolean;
  loadingTable: boolean;
};

declare module "@mui/x-data-grid" {
  interface FooterPropsOverrides {
    onClick: () => void;
    loading: boolean;
  }
}

function CustomFooterAddButtonComponent(props: NonNullable<GridSlotsComponentsProps["footer"]>) {
  const [clickedWhileLoading, setClickedWhileLoading] = useState(false); // this displays a loading symbol on the button only if clicked while not ready yet

  const loading = props.loading;
  const onClick = () => {
    props.onClick?.();
  };
  useEffect(() => {
    if (!loading && clickedWhileLoading) {
      setClickedWhileLoading(false);
      onClick();
    }
  }, [loading, setClickedWhileLoading, clickedWhileLoading]);

  return (
    <Box sx={{ display: "flex" }} className="highlight-for-espp-tour-step-1">
      <LoadingButton
        onClick={() => {
          if (props.loading) {
            setClickedWhileLoading(true);
          } else if (props.onClick !== undefined) {
            setClickedWhileLoading(false);
            onClick();
          }
        }}
        variant="outlined"
        size="large"
        startIcon={<AddIcon />}
        loading={clickedWhileLoading}
        sx={{ flexGrow: 1 }}
      >
        Add An ESPP Plan
      </LoadingButton>
    </Box>
  );
}

function ESPPTable(props: ESPPTableProps) {
  const theme = useTheme();
  const dataGridApiRef = useGridApiRef<GridApiPro>();

  const tableHeaderMap: { [key: string]: keyof ESPP } = {
    "Offering Date": "offering_date",
    "Purchase Period Start": "purchase_period_start",
    "Purchase Date": "purchase_date",
    "Contribution Amount": "contribution_amount",
    "Discount Rate": "discount_rate",
    "Has Lookback": "has_lookback",
    "Qualified Plan": "qualified_plan",
    "Effective Purchase Price": "effective_purchase_price",
    "ESPP Extra Value": "alpha",
  };
  const tableHeaderList = Object.keys(tableHeaderMap);

  const tableHeaderFormatters: { [key: string]: (value: any) => string } = {
    "Offering Date": (value: any) => formatDate(value),
    "Purchase Period Start": (value: any) => formatDate(value),
    "Purchase Date": (value: any) => formatDate(value),
    "Contribution Amount": (value: any) => formatDollars(value),
    "Discount Rate": (value: any) => formatPercent(value),
    "Has Lookback": (value: any) => (value ? "Yes" : "No"),
    "Qualified Plan": (value: any) => (value ? "Yes" : "No"),
    "Effective Purchase Price": (value: any) => formatPrice(value),
    "ESPP Extra Value": (value: any) => formatDollars(value),
  };

  const rowsNoTotal = props.esppPlans.map((espp) => createDataFromESPP(espp, tableHeaderMap, tableHeaderFormatters));

  function dateRowCellRender(params: GridRenderCellParams, index: number) {
    const currData = params.row.data[params.field];
    let tooltipText = "";
    let cellData = <div>{currData.formattedValue}</div>;

    const boxContents = (
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", flexGrow: 1 }}>{cellData}</Box>
    );

    if (tooltipText) {
      return (
        <NoWrapTooltip
          title={<div style={{ whiteSpace: "pre-line", fontSize: "1rem" }}>{tooltipText}</div>}
          enterDelay={enterToolTipDelay}
          arrow
          placement="bottom"
        >
          {boxContents}
        </NoWrapTooltip>
      );
    }

    return boxContents;
  }

  const removeDefaultGrayBackgroundTheme = createTheme({
    palette: {
      background: {
        default: "#ffffff",
      },
    },
  });
  // Styling for the data table
  const dataGridSX = {
    " & .all-grants-summary-row-label": {
      fontWeight: "bold",
      fontSize: bigFontSize,
    },
    "& .regular-row-label": {
      fontWeight: "bold",
      fontSize: bigFontSize,
      borderBottom: "none",
    },

    "& .all-grants-summary-row-cells": {
      fontSize: bigFontSize,
      fontWeight: "bold",
      color: theme.palette.info.main,
    },
    "& .summary-column-cell": {
      fontSize: bigFontSize,
      color: theme.palette.primary.main,
      borderBottom: "none",
    },
    "& .regular-column-cell": {
      fontSize: mediumFontSize,
      borderBottom: "none",
    },
  }; //TODO: add more classes for already vested and future vesting? (be wary of too many colors)

  // TODO: add no rows overlay

  const dataGridColumns: GridColDef[] = [
    {
      field: "rowNames",
      headerName: "",
      cellClassName: (params: GridCellParams) =>
        params.id === allGrantsString && params.rowNode.type === "pinnedRow"
          ? "all-grants-summary-row-label"
          : "regular-row-label",
      minWidth: rowNameWidth, // 150,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => {
        const isOverallTotalRow = params.id === allGrantsString && params.rowNode.type === "pinnedRow";
        const pctVested = params.row.data["percentVested"].dataValue;
        return (
          <>
            {params.rowNode.type === "pinnedRow" ? null : (
              <IconButton onClick={() => props.onStockPlanEdit(params.row.name)}>
                <EditIcon />
              </IconButton>
            )}
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
              {params.row.name}
              {isNaN(pctVested) ? (
                <div>
                  <br />
                </div>
              ) : (
                <Box
                  alignItems="center"
                  sx={{ display: "flex", flexDirection: "row", width: rowNameWidth - buttonWidth - 24 }}
                >
                  <Box sx={{ width: "100%", mr: 1 }}>
                    <LinearProgress
                      variant="determinate"
                      value={pctVested * 100}
                      sx={{
                        width: rowNameWidth - buttonWidth - 100,
                        height: 15,
                        border: 0.5,
                        borderRadius: 1,
                        borderColor:
                          pctVested > 0.99
                            ? theme.palette.success.light
                            : isOverallTotalRow
                            ? theme.palette.info.light
                            : theme.palette.primary.light,
                        // borderColor: theme.palette.text.primary,
                        // backgroundColor: theme.palette.text.disabled,
                        backgroundColor: theme.palette.background.default,
                        "& .MuiLinearProgress-bar": {
                          backgroundColor:
                            pctVested > 0.99
                              ? theme.palette.success.main
                              : isOverallTotalRow
                              ? theme.palette.info.main
                              : theme.palette.primary.main,
                        },
                      }}
                    />
                  </Box>
                  <Typography color="text.primary" fontSize={mediumFontSize}>
                    {pctVested.toLocaleString("en-US", { style: "percent", maximumFractionDigits: 0 }) + " vested"}
                  </Typography>
                </Box>
              )}
            </Box>
          </>
        );
      },
    },
    ...tableHeaderList.map((headerString, index) => ({
      field: headerString,
      align: "right" as const,
      headerAlign: "right" as const,
      headerClassName: "vest-header-class",
      cellClassName: (params: GridCellParams) =>
        params.id === allGrantsString && params.rowNode.type === "pinnedRow"
          ? "all-grants-summary-row-cells"
          : "regular-column-cell",
      renderHeader: (params: GridColumnHeaderParams) => {
        return (
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end" }}>
            <Typography
              sx={{
                fontWeight: "bold",
                fontSize: bigFontSize,
                whiteSpace: "normal",
                wordWrap: "break-word",
                textAlign: "center",
              }}
            >
              {headerString}
            </Typography>
          </Box>
        );
      },
      minWidth: 150,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => {
        return dateRowCellRender(params, index);
      },
    })),
  ];

  const dataGridAutosizeOptions = {
    includeOutliers: true,
    includeHeaders: true,
  };

  return (
    <Paper sx={{ padding: paperPadding, position: "relative" }} className="highlight-for-espp-tour-step-2">
      {/*Have to import these weights to style the table header*/}
      <ThemeProvider theme={deepmerge(theme, removeDefaultGrayBackgroundTheme)}>
        <DataGridPro
          className="tour-step-1"
          apiRef={dataGridApiRef}
          slots={{
            footer: CustomFooterAddButtonComponent,
          }}
          slotProps={{
            footer: { onClick: props.startAdd, loading: props.loadingAddStockPlan },
          }}
          disableColumnMenu
          loading={props.loadingTable} // TODO: set up a loading wheel when loading
          sx={dataGridSX}
          getRowId={(row) => row.name}
          rows={rowsNoTotal}
          initialState={{
            pinnedColumns: {
              left: ["rowNames"],
            },
          }}
          columns={dataGridColumns}
          autosizeOptions={dataGridAutosizeOptions}
        />
      </ThemeProvider>
      <Box
        sx={{
          pb: 1,
        }}
      >
        <AsOf referenceDateString={props.referenceDateString} />
      </Box>
    </Paper>
  );
}

export default ESPPTable;
